<!--
  AttachmentInput — the form renderer for file fields: `attachment` and its
  image / document / video subtypes. It hosts the grid's AttachmentField, giving
  it the row and writer a GridCell would: at rest the field's thumbnails (click
  to view, × to remove, drop to upload); the "+" opens the editor to add from
  the library or the computer.

  Every write leaves as the field's complete id set on `update:modelValue`,
  which the host persists (useRecord.updateField). The editor's picks and
  removals are a draft, committed when it closes; Escape discards them.
-->
<template>
  <div
    ref="rootEl"
    class="input chip-input attachment-input"
    :class="{ 'is-editing': editing }"
    @click="onClick"
    @keydown.esc.stop="close(false)"
  >
    <AttachmentField
      :model-value="editing ? draft : entities"
      :field="field"
      :mode="editing ? 'editing' : 'active'"
      @update:model-value="draft = $event"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, provide, ref } from "vue"
import type { Field } from "@auger/core/fields"
import { CELL_CONTEXT } from "@auger/ui/composables/useInteraction"
import type { ReferenceEntity as Entity } from "."
import AttachmentField from "./AttachmentField.vue"

const props = withDefaults(
  defineProps<{
    modelValue?: unknown
    item?: Record<string, unknown> | null
    field: Field
    readonly?: boolean
  }>(),
  { modelValue: undefined, item: null, readonly: false }
)

const emit = defineEmits<{
  "update:modelValue": [value: (number | string)[] | number | string | null]
}>()

const entities = computed<Entity[]>(() => {
  const resolved = props.field.value(props.item)
  const list = resolved == null ? [] : Array.isArray(resolved) ? resolved : [resolved]
  return list.filter((e): e is Entity => !!e && typeof e === "object" && "id" in e)
})

function save(next: Entity[]) {
  const ids = next.map((e) => e.id)
  emit("update:modelValue", props.field.isArray ? ids : (ids.at(-1) ?? null))
}

// A delta lands on the value as it is when it arrives: an upload can finish
// after other edits. A single file field takes the newest file.
function write({ add = [], remove = [] }: { add?: Entity[]; remove?: Entity[] }) {
  const gone = new Set(remove.map((e) => String(e.id)))
  const kept = entities.value.filter((e) => !gone.has(String(e.id)))
  const have = new Set(kept.map((e) => String(e.id)))
  const added = add.filter((e) => !have.has(String(e.id)))
  save(props.field.isArray || !added.length ? [...kept, ...added] : added.slice(-1))
  return Promise.resolve()
}

provide(CELL_CONTEXT, {
  item: () => props.item ?? {},
  writer: () => (props.readonly || !props.field.isEditable ? null : write),
})

// --- Editor -----------------------------------------------------------------------

const rootEl = ref<HTMLElement | null>(null)
const editing = ref(false)
const draft = ref<unknown>(null)

function onClick(event: MouseEvent) {
  if (editing.value || !(event.target as HTMLElement).closest(".add")) return
  draft.value = entities.value
  editing.value = true
  document.addEventListener("mousedown", onDocumentMousedown, true)
}

function close(commit: boolean) {
  if (!editing.value) return
  editing.value = false
  document.removeEventListener("mousedown", onDocumentMousedown, true)
  const next = (Array.isArray(draft.value) ? draft.value : [draft.value]).filter(
    (e): e is Entity => !!e && typeof e === "object" && "id" in e
  )
  const ids = (list: Entity[]) => list.map((e) => String(e.id)).join()
  if (commit && ids(next) !== ids(entities.value)) save(next)
}

// A press outside the box commits, except in the file viewer the editor opens.
function onDocumentMousedown(event: MouseEvent) {
  const target = event.target as HTMLElement
  if (rootEl.value?.contains(target) || target.closest?.(".file-viewer")) return
  close(true)
}

onBeforeUnmount(() => document.removeEventListener("mousedown", onDocumentMousedown, true))
</script>

<style>
.attachment-input {
  height: auto;
  padding: 4px;

  .attachment-field {
    --thumb: 64px;
  }

  .attachment-field.is-active .thumbs {
    align-items: center;
  }

  .attachment-field .add {
    width: 32px;
    height: 32px;
  }

  &.is-editing .attachment-field {
    --thumb: 72px;
  }
}
</style>
